'use client';

import * as React from 'react';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './sheet';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from './heading';
import { LuX } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

type ModalClassName = {
  header?: string;
  content?: string;
  overlayClassName?: string;
};

type ModalContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  close: () => void;
};

const ModalContext = React.createContext<ModalContextValue | null>(null);

export function useModal() {
  return React.useContext(ModalContext);
}

type ModalProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  title?: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: ModalClassName;
  sheetSide?: React.ComponentProps<typeof SheetContent>['side'];
  modal?: boolean;
  showCustomClose?: boolean;
  headerExtra?: React.ReactNode;
} & Omit<React.ComponentProps<typeof Dialog>, 'open' | 'onOpenChange'> &
  Omit<React.ComponentProps<typeof Sheet>, 'open' | 'onOpenChange'>;

export function Modal({
  open: openProp,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  className,
  sheetSide = 'bottom',
  modal = false,
  headerExtra,
  showCustomClose,
  ...rest
}: ModalProps) {
  const { t } = useTranslations();
  const isMobile = useMediaQuery('(max-width: 639px)');
  const isControlled = openProp !== undefined;

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);

  const open = isControlled ? openProp : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const ctx = React.useMemo<ModalContextValue>(
    () => ({
      open,
      setOpen,
      close: () => setOpen(false),
    }),
    [open, setOpen],
  );

  if (isMobile) {
    return (
      <ModalContext.Provider value={ctx}>
        <Sheet open={open} onOpenChange={setOpen} {...rest}>
          {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
          <SheetContent
            closeIcon={{ wrapper: 'hidden' }}
            overlayClassName={cn('bg-black/30 backdrop-blur-none', className?.overlayClassName)}
            side={sheetSide}
            className={cn(
              'w-full rounded-b-none rounded-t-[4px] border-none px-4 py-6',
              className?.content,
            )}
          >
            {title && showCustomClose && (
              <SheetHeader
                className={cn(
                  'flex w-full flex-row items-center justify-between gap-4 sm:gap-6',
                  'ea-modal__sheet-header',
                  'ea-modal__header',
                  className?.header,
                )}
              >
                <SheetTitle>
                  <Heading tag="h4" className={{ title: 'uppercase' }} title={title} />
                </SheetTitle>
                <ModalClose aria-label={t('text272')}>
                  <LuX size={24} aria-hidden />
                </ModalClose>
              </SheetHeader>
            )}
            {children}
          </SheetContent>
        </Sheet>
      </ModalContext.Provider>
    );
  }

  return (
    <ModalContext.Provider value={ctx}>
      <Dialog open={open} onOpenChange={setOpen} {...rest}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          hideClose
          overlayClassName={cn('bg-black/30 backdrop-blur-none', className?.overlayClassName)}
          className={cn(
            'w-full max-w-[792px] rounded-none border-none sm:max-w-[792px]',
            className?.content,
          )}
          onInteractOutside={modal ? e => e.preventDefault() : undefined}
          onEscapeKeyDown={modal ? e => e.preventDefault() : undefined}
        >
          {title && showCustomClose && (
            <DialogHeader
              className={cn(
                'flex w-full flex-row items-start justify-between gap-4 sm:gap-6',
                'ea-modal__dialog-header',
                'ea-modal__header',
                className?.header,
              )}
            >
              <DialogTitle>
                <Heading tag="h4" className={{ title: 'uppercase' }} title={title} />
              </DialogTitle>
              {/* /images/icons/close-square.svg does not exist in public/ — the button rendered empty. */}
              <ModalClose aria-label={t('text272')}>
                <LuX size={24} aria-hidden />
              </ModalClose>
            </DialogHeader>
          )}
          {children}
        </DialogContent>
      </Dialog>
    </ModalContext.Provider>
  );
}

type ModalCloseProps = React.ComponentProps<typeof DialogClose> &
  React.ComponentProps<typeof SheetClose>;

export function ModalClose(props: ModalCloseProps) {
  const isMobile = useMediaQuery('(max-width: 639px)');
  if (isMobile) return <SheetClose {...props} />;
  return <DialogClose {...props} />;
}
